import { useChatFilterContext } from "contexts/ChatFilterContext";
import React, { useContext, useEffect, useMemo } from "react";
import { useChatContext } from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { Channel, ChannelFilters, ChannelSort } from "stream-chat";
import { ChannelSearchList } from "components/ChannelSearchList";
import { EmptyChannelList } from "components/EmptyChannelList";
import Loader from "components/DesignSystem/Loader/loader";
import { filterExcludedChannels } from "util/isChannelLastMessageExcluded";
import { ChatFilterAccordion } from "./ChatFilterAccordion";
import { usePaginatedChannels } from "hooks/usePaginatedChannels";
import { SelectedChannelsContext } from "./SelectedChannelContext";

export const SpocUnrepliedView = ({
  filters,
  sort,
  onSelectCallback,
  unreplied,
  spocUnavailableGroups,
  onChannelUpdated,
}: {
  filters: ChannelFilters<DefaultStreamChatGenerics>;
  sort: ChannelSort<DefaultStreamChatGenerics>;
  onSelectCallback: (channelId?: string, messageId?: string) => void;
  unreplied: boolean;
  spocUnavailableGroups: string[];
  onChannelUpdated: (
    setChannels: React.Dispatch<
      React.SetStateAction<Array<Channel<DefaultStreamChatGenerics>>>
    >
  ) => any;
}) => {
  const { client, channelsQueryState } = useChatContext();
  const channelFilters = {
    ...filters,
    group_uuid: { $in: spocUnavailableGroups },
  };

  const { channels, hasNextPage, loadNextPage } = usePaginatedChannels(
    client,
    channelFilters,
    sort,
    { limit: 15 },
    () => {},
    5000,
    undefined,
    onChannelUpdated
  );

  const {
    filters: { EXCLUDE: excludeOptions },
  } = useChatFilterContext();
  const visibleChannels = useMemo(
    () => filterExcludedChannels(channels, excludeOptions),
    [channels, excludeOptions]
  );

  const { setChannelsForSource } = useContext(SelectedChannelsContext);
  const channelInfos = visibleChannels
    .filter((c) => Boolean(c.id))
    .map((c) => ({
      id: c.id as string,
      needsReply: Boolean(c.data?.needs_reply),
    }));
  const channelInfosKey = channelInfos
    .map((c) => `${c.id}:${c.needsReply ? 1 : 0}`)
    .join(",");

  useEffect(() => {
    setChannelsForSource("spocUnreplied", channelInfos);
  }, [channelInfosKey]);

  useEffect(
    () => () => setChannelsForSource("spocUnreplied", []),
    [setChannelsForSource]
  );

  if (channelsQueryState.queryInProgress === "reload") {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (visibleChannels.length === 0) {
    return (
      <ChatFilterAccordion
        title="SPOC Unavailable"
        contentId="spocUnrepliedScrollParent"
        isEmpty={true}
      >
        <EmptyChannelList />
      </ChatFilterAccordion>
    );
  }

  return (
    <ChatFilterAccordion
      title="SPOC Unavailable"
      contentId="spocUnrepliedScrollParent"
      isEmpty={false}
      takesFullHeight={visibleChannels.length >= 15}
    >
      <ChannelSearchList
        searchValue=""
        onSelect={onSelectCallback}
        unreplied={unreplied}
        windowElementId="spocUnrepliedScrollParent"
        channels={visibleChannels}
        hasNextPage={hasNextPage}
        loadNextPage={loadNextPage}
      />
    </ChatFilterAccordion>
  );
};
